import React, { useEffect } from 'react';
import { SimpleGrid, Spinner, Flex, Global, css } from '@chakra-ui/core';
import { useHistory, useParams, useRouteMatch } from 'react-router-dom';
import { useStoreState } from '../../../../models/hooks';
import { QuestionnaireTemplateStore } from '../store/questionnaireTemplateModel';
import { TEMPLATE_MODE } from '../questionnaireTemplate.enums';
import { FormBuilderArea } from './formBuilderComponents/FormBuilderArea';
import { PreviewSidebar } from './previewSidebar/PreviewSidebar';
import { useCurrentUserProfile } from '../../../../app/hooks/useCurrentUserProfile';
import {
  PATH_ADMINISTRATION,
  PATH_QUESTIONNAIRE_TEMPLATES,
  PATH_QUESTIONNAIRE_TEMPLATE_COPY,
  PATH_QUESTIONNAIRE_TEMPLATE_EDIT,
} from '../../../../routes/constants';
import { Header } from '../../../../app/topBar/Header';

const FormBuilderInner = () => {
  const { templateId } = useParams<{ templateId?: string }>();
  const isEditRoute = !!useRouteMatch({ path: PATH_QUESTIONNAIRE_TEMPLATE_EDIT, exact: true });
  const isCopyRoute = !!useRouteMatch({ path: PATH_QUESTIONNAIRE_TEMPLATE_COPY, exact: true });

  const { currentUserProfile } = useCurrentUserProfile();
  const accountId = currentUserProfile?.account ?? '';
  const isLoading = QuestionnaireTemplateStore.useStoreState((s) => s.isLoading);
  const commitSnapshot = QuestionnaireTemplateStore.useStoreActions((a) => a.commitSnapshot);

  const fetchTemplate = QuestionnaireTemplateStore.useStoreActions((a) => a.fetchTemplate);
  const setTemplateMode = QuestionnaireTemplateStore.useStoreActions((a) => a.setTemplateMode);
  const reset = QuestionnaireTemplateStore.useStoreActions((a) => a.reset);

  // useUnsavedChanges();

  useEffect(() => {
    if ((isEditRoute || isCopyRoute) && templateId && accountId) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      fetchTemplate({ accountId, templateId, isCopy: isCopyRoute });
    } else {
      setTemplateMode(TEMPLATE_MODE.CREATE);
      commitSnapshot();
    }

    return () => {
      reset();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId, isEditRoute, isCopyRoute, accountId]);

  if (isLoading) {
    return (
      <Flex align="center" justify="center" height="100%" minH="400px">
        <Spinner size="xl" />
      </Flex>
    );
  }

  return (
    <SimpleGrid templateColumns="1fr 400px" height="calc(100dvh - 64px)">
      <FormBuilderArea />
      <PreviewSidebar />
    </SimpleGrid>
  );
};

export const QuestionnaireFormBuilder = () => {
  const httpClient = useStoreState((state) => state.http.client);
  const history = useHistory();
  const accounts = useStoreState((s) => s.app.accounts);
  const userId = useStoreState((s) => s.app.user?.id);
  const isQuestionnaireTemplateEnabled = useStoreState((s) => s.app.accounts?.configuration?.questionTemplateEnabled);

  const owners = accounts?.owners ?? [];
  const superOwners = accounts?.superOwners ?? [];

  const isAdmin = React.useCallback((id: string) => owners.includes(id), [owners]);
  const isSuperAdmin = React.useCallback((id: string) => superOwners.includes(id), [superOwners]);

  React.useEffect(() => {
    if (!userId || !accounts) return;

    if (!isQuestionnaireTemplateEnabled) {
      history.replace(PATH_ADMINISTRATION);
      return;
    }

    if (!isSuperAdmin(userId) && !isAdmin(userId)) {
      history.replace(PATH_QUESTIONNAIRE_TEMPLATES);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isQuestionnaireTemplateEnabled, accounts, isSuperAdmin, isAdmin, userId]);

  return (
    <>
      <Header showMenu />
      <GlobalStyles />
      <QuestionnaireTemplateStore.Provider initialData={{ httpClient }}>
        <FormBuilderInner />
      </QuestionnaireTemplateStore.Provider>
    </>
  );
};

const GlobalStyles = () => (
  <Global
    styles={css`
      html {
        overflow: hidden;
      }
      main {
        min-height: 0;
        height: 100dvh;
        display: flex !important;
        flex-direction: column;
        position: fixed;
        width: 100dvw;
      }
    `}
  />
);
